import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import ReplayIcon from '@mui/icons-material/Replay'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { TabelaAcao } from '@/components/common/TabelaAcao'
import { EstadoDeLista, EstadoDeListaTipo } from '@/components/common/EstadoDeLista'
import { authStore } from '@/store/authStore'
import { tokens } from '@/theme'
import { notificacoesAdminService } from '@/services/notificacoesAdminService'
import {
  CANAIS_NOTIFICACAO,
  EVENTOS_NOTIFICACAO,
  NotificacaoAdmin,
  NotificacaoAdminDetalhe,
  NotificacaoAdminResumo,
  NotificacaoHistoricoFiltro,
  STATUS_NOTIFICACAO,
  StatusNotificacao,
} from '@/types/notificacaoAdmin'

// O backend recebe LocalDateTime (sem fuso): `toISOString()` deslocaria a janela em 3h.
const LOCAL_DATE_TIME = 'YYYY-MM-DDTHH:mm:ss'

const STATUS_LABEL: Record<StatusNotificacao, string> = {
  PENDENTE: 'Pendente',
  ENVIADA: 'Enviada',
  FALHA: 'Falha',
  LIDA: 'Lida',
}

const STATUS_COR: Record<StatusNotificacao, string> = {
  PENDENTE: tokens.statusWarning,
  ENVIADA: tokens.brandPrimary,
  FALHA: tokens.statusDanger,
  LIDA: tokens.statusSuccess,
}

const CANAL_LABEL: Record<string, string> = {
  EMAIL: 'E-mail',
  SMS: 'SMS',
  PUSH: 'Push',
  IN_APP: 'No app',
}

const rotuloEvento = (evento: string) =>
  evento
    .toLowerCase()
    .split('_')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ')

const formatarData = (valor?: string | null) =>
  valor ? dayjs(valor).format('DD/MM/YYYY HH:mm') : '—'

const rotuloDestinatario = (d: string) =>
  d.startsWith('PAPEL:') ? `Fila ${d.slice('PAPEL:'.length)}` : d

const estadoDoErro = (e: unknown): EstadoDeListaTipo =>
  (e as { status?: number })?.status === 403 ? 'semPermissao' : 'erro'

const FILTRO_INICIAL: NotificacaoHistoricoFiltro = {
  status: '',
  canal: '',
  evento: '',
  destinatario: '',
  de: '',
  ate: '',
}

/**
 * Central de notificações do back-office (Figma 7.4 / A14): KPIs, filtros, histórico
 * global com "Reprocessar" para falhas (só ADMIN) e drawer com payload, tentativas e DLQ.
 * OPERADOR/GERENTE enxergam; a rota é despachada por papel em `router/rotas/a14.tsx`.
 */
const NotificacoesAdminPage: FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const usuario = authStore((s) => s.usuario)
  const ehAdmin = useMemo(
    () => !!usuario?.permissoes?.some((p) => p.toUpperCase() === 'ADMIN'),
    [usuario]
  )

  const [filtro, setFiltro] = useState<NotificacaoHistoricoFiltro>(FILTRO_INICIAL)
  const [filtroAplicado, setFiltroAplicado] = useState<NotificacaoHistoricoFiltro>(FILTRO_INICIAL)
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)

  const [linhas, setLinhas] = useState<NotificacaoAdmin[]>([])
  const [total, setTotal] = useState(0)
  const [resumo, setResumo] = useState<NotificacaoAdminResumo | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [estadoErro, setEstadoErro] = useState<EstadoDeListaTipo | null>(null)

  const [detalhe, setDetalhe] = useState<NotificacaoAdminDetalhe | null>(null)
  const [detalheCarregando, setDetalheCarregando] = useState(false)
  const [reprocessando, setReprocessando] = useState<string | null>(null)

  const paramsDoFiltro = useCallback((f: NotificacaoHistoricoFiltro): NotificacaoHistoricoFiltro => {
    return {
      ...f,
      de: f.de ? dayjs(f.de).startOf('day').format(LOCAL_DATE_TIME) : '',
      ate: f.ate ? dayjs(f.ate).endOf('day').format(LOCAL_DATE_TIME) : '',
    }
  }, [])

  const carregar = useCallback(async () => {
    setCarregando(true)
    setEstadoErro(null)
    try {
      const f = paramsDoFiltro(filtroAplicado)
      const [pagina, kpis] = await Promise.all([
        notificacoesAdminService.historico(f, page, size),
        notificacoesAdminService.resumo(f),
      ])
      setLinhas(pagina.content ?? [])
      setTotal(pagina.totalElements ?? 0)
      setResumo(kpis)
    } catch (e) {
      setEstadoErro(estadoDoErro(e))
    } finally {
      setCarregando(false)
    }
  }, [filtroAplicado, page, size, paramsDoFiltro])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const aplicarFiltro = () => {
    setPage(0)
    setFiltroAplicado(filtro)
  }

  const limparFiltro = () => {
    setFiltro(FILTRO_INICIAL)
    setFiltroAplicado(FILTRO_INICIAL)
    setPage(0)
  }

  const abrir = async (n: NotificacaoAdmin) => {
    setDetalheCarregando(true)
    try {
      setDetalhe(await notificacoesAdminService.detalhe(n.id))
    } catch {
      enqueueSnackbar('Não foi possível carregar o detalhe da notificação', { variant: 'error' })
    } finally {
      setDetalheCarregando(false)
    }
  }

  const reprocessar = async (n: NotificacaoAdmin) => {
    setReprocessando(n.id)
    try {
      const r = await notificacoesAdminService.reprocessar(n.id)
      enqueueSnackbar(r.mensagem, {
        variant: r.reprocessada
          ? r.notificacao.status === 'ENVIADA'
            ? 'success'
            : 'warning'
          : 'info',
      })
      setLinhas((atual) => atual.map((x) => (x.id === n.id ? r.notificacao : x)))
      if (detalhe?.notificacao.id === n.id) {
        setDetalhe(await notificacoesAdminService.detalhe(n.id))
      }
      const kpis = await notificacoesAdminService.resumo(paramsDoFiltro(filtroAplicado))
      setResumo(kpis)
    } catch (e) {
      enqueueSnackbar(
        (e as { status?: number })?.status === 403
          ? 'Reprocessar é restrito a administradores'
          : 'Não foi possível reprocessar a notificação',
        { variant: 'error' }
      )
    } finally {
      setReprocessando(null)
    }
  }

  const chipStatus = (status: StatusNotificacao) => (
    <Chip
      size="small"
      label={STATUS_LABEL[status]}
      sx={{
        color: STATUS_COR[status],
        borderColor: STATUS_COR[status],
        fontWeight: 600,
      }}
      variant="outlined"
    />
  )

  const celulaStatus = (n: NotificacaoAdmin) => (
    <Stack direction="row" spacing={1} alignItems="center">
      {chipStatus(n.status)}
      {n.status === 'FALHA' && ehAdmin && (
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<ReplayIcon fontSize="small" />}
          disabled={reprocessando === n.id}
          onClick={(ev) => {
            ev.stopPropagation()
            void reprocessar(n)
          }}
        >
          Reprocessar
        </Button>
      )}
    </Stack>
  )

  const conteudo = () => {
    if (carregando && linhas.length === 0) {
      return <EstadoDeLista estado="carregando" />
    }
    if (estadoErro) {
      return (
        <EstadoDeLista
          estado={estadoErro}
          {...(estadoErro === 'semPermissao'
            ? { detalhe: 'A central de notificações é do back-office (OPERADOR, GERENTE ou ADMIN).' }
            : { acao: { label: 'Tentar novamente', onClick: () => void carregar() } })}
        />
      )
    }
    if (linhas.length === 0) {
      return (
        <EstadoDeLista
          estado="vazio"
          mensagem="Nenhuma notificação encontrada"
          detalhe="Ajuste os filtros ou aguarde novos eventos."
        />
      )
    }
    return (
      <>
        <TabelaAcao<NotificacaoAdmin>
          aria-label="Histórico de notificações"
          linhas={linhas}
          chaveDaLinha={(n) => n.id}
          onAbrir={(n) => void abrir(n)}
          colunas={[
            {
              titulo: 'Evento',
              render: (n) => (
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {rotuloEvento(n.evento)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: tokens.textSecondary }}>
                    {n.titulo}
                  </Typography>
                </Box>
              ),
            },
            {
              titulo: 'Destinatário',
              render: (n) => (
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {rotuloDestinatario(n.destinatario)}
                </Typography>
              ),
            },
            { titulo: 'Canal', render: (n) => CANAL_LABEL[n.canal] ?? n.canal },
            { titulo: 'Status', render: celulaStatus },
            { titulo: 'Enviado em', render: (n) => formatarData(n.enviadaEm ?? n.criadaEm) },
          ]}
        />
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={size}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(ev) => {
            setSize(parseInt(ev.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage="Por página"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
        />
      </>
    )
  }

  return (
    <Box>
      <PageHeader
        title="Notificações"
        eyebrow="Conta"
        subtitle="Central de notificações: entregas, falhas e reprocessamento"
        acao={
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => void carregar()}
            disabled={carregando}
          >
            Atualizar
          </Button>
        }
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Enviadas"
            variante="escura"
            valor={resumo?.enviadas ?? 0}
            legenda={`${resumo?.pendentes ?? 0} pendente(s)`}
            carregando={carregando && !resumo}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Não lidas"
            valor={resumo?.naoLidas ?? 0}
            legenda="Enviadas sem confirmação de leitura (e-mail/SMS não confirmam)"
            carregando={carregando && !resumo}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Falhas"
            valor={resumo?.falhas ?? 0}
            legenda={ehAdmin ? 'Reprocessáveis na tabela' : 'Reprocessamento é do ADMIN'}
            carregando={carregando && !resumo}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Templates"
            valor={resumo?.templates ?? 0}
            legenda="Um por tipo de evento"
            carregando={carregando && !resumo}
          />
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={filtro.status ?? ''}
              onChange={(ev) =>
                setFiltro({ ...filtro, status: ev.target.value as StatusNotificacao | '' })
              }
            >
              <MenuItem value="">Todos</MenuItem>
              {STATUS_NOTIFICACAO.map((s) => (
                <MenuItem key={s} value={s}>
                  {STATUS_LABEL[s]}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Canal"
              value={filtro.canal ?? ''}
              onChange={(ev) =>
                setFiltro({ ...filtro, canal: ev.target.value as NotificacaoHistoricoFiltro['canal'] })
              }
            >
              <MenuItem value="">Todos</MenuItem>
              {CANAIS_NOTIFICACAO.map((c) => (
                <MenuItem key={c} value={c}>
                  {CANAL_LABEL[c]}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Evento"
              value={filtro.evento ?? ''}
              onChange={(ev) => setFiltro({ ...filtro, evento: ev.target.value })}
            >
              <MenuItem value="">Todos</MenuItem>
              {EVENTOS_NOTIFICACAO.map((e) => (
                <MenuItem key={e} value={e}>
                  {rotuloEvento(e)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Destinatário"
              placeholder="e-mail, id ou PAPEL"
              value={filtro.destinatario ?? ''}
              onChange={(ev) => setFiltro({ ...filtro, destinatario: ev.target.value })}
              onKeyDown={(ev) => ev.key === 'Enter' && aplicarFiltro()}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="De"
              InputLabelProps={{ shrink: true }}
              value={filtro.de ?? ''}
              onChange={(ev) => setFiltro({ ...filtro, de: ev.target.value })}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={1.5}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Até"
              InputLabelProps={{ shrink: true }}
              value={filtro.ate ?? ''}
              onChange={(ev) => setFiltro({ ...filtro, ate: ev.target.value })}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={aplicarFiltro}>
                Filtrar
              </Button>
              <Button variant="text" onClick={limparFiltro}>
                Limpar
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Paper>{conteudo()}</Paper>

      {/* Drawer: payload, tentativas, erro e situação na DLQ (Figma 7.4). */}
      <Drawer
        anchor="right"
        open={!!detalhe || detalheCarregando}
        onClose={() => setDetalhe(null)}
        PaperProps={{ role: 'dialog', 'aria-label': 'Detalhe da notificação', sx: { width: { xs: '100%', sm: 480 }, p: 3 } }}
      >
        {detalheCarregando && !detalhe && <EstadoDeLista estado="carregando" />}
        {detalhe && (
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">{rotuloEvento(detalhe.notificacao.evento)}</Typography>
              <IconButton aria-label="Fechar" onClick={() => setDetalhe(null)}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {chipStatus(detalhe.notificacao.status)}
              <Chip size="small" label={CANAL_LABEL[detalhe.notificacao.canal] ?? detalhe.notificacao.canal} />
              {detalhe.naDlq && (
                <Chip
                  size="small"
                  label="Na DLQ"
                  sx={{ backgroundColor: tokens.statusDanger, color: tokens.surfaceCard, fontWeight: 600 }}
                />
              )}
            </Stack>

            <Divider />

            <Box>
              <Typography variant="overline" sx={{ color: tokens.textSecondary }}>
                Destinatário
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {rotuloDestinatario(detalhe.notificacao.destinatario)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="overline" sx={{ color: tokens.textSecondary }}>
                Payload
              </Typography>
              <Typography variant="subtitle2">{detalhe.notificacao.titulo}</Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  mt: 0.5,
                  backgroundColor: tokens.surfacePage,
                  whiteSpace: 'pre-wrap',
                  fontSize: 13,
                  maxHeight: 240,
                  overflow: 'auto',
                }}
              >
                {detalhe.notificacao.conteudo}
              </Paper>
            </Box>

            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="overline" sx={{ color: tokens.textSecondary }}>
                  Tentativas
                </Typography>
                <Typography variant="body2">
                  {detalhe.notificacao.tentativas} de {detalhe.maxTentativasAutomaticas} automáticas
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="overline" sx={{ color: tokens.textSecondary }}>
                  Criada em
                </Typography>
                <Typography variant="body2">{formatarData(detalhe.notificacao.criadaEm)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="overline" sx={{ color: tokens.textSecondary }}>
                  Enviada em
                </Typography>
                <Typography variant="body2">{formatarData(detalhe.notificacao.enviadaEm)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="overline" sx={{ color: tokens.textSecondary }}>
                  Lida em
                </Typography>
                <Typography variant="body2">{formatarData(detalhe.notificacao.lidaEm)}</Typography>
              </Grid>
            </Grid>

            {detalhe.notificacao.erro && (
              <Alert severity="error" variant="outlined">
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>
                  Último erro
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                  {detalhe.notificacao.erro}
                </Typography>
              </Alert>
            )}

            <Alert severity={detalhe.naDlq ? 'warning' : 'info'} variant="outlined">
              {detalhe.naDlq
                ? `Retentativas automáticas esgotadas — só sai daqui por "Reprocessar" (ADMIN).`
                : `Eventos que nunca viraram notificação ficam no tópico ${detalhe.topicoDlq} do Kafka e não aparecem nesta lista.`}
            </Alert>

            {detalhe.reprocessavel && ehAdmin && (
              <Button
                variant="contained"
                color="error"
                startIcon={<ReplayIcon />}
                disabled={reprocessando === detalhe.notificacao.id}
                onClick={() => void reprocessar(detalhe.notificacao)}
              >
                Reprocessar
              </Button>
            )}
          </Stack>
        )}
      </Drawer>
    </Box>
  )
}

export default NotificacoesAdminPage
